import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CornerDownLeft, Search } from 'lucide-react'

import { NAV_ITEMS, type NavItem } from '@/components/layout/nav-config'
import { useFocusTrap } from '@/lib/keyboard/use-focus-trap'
import { cn } from '@/lib/utils'

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function filterItems(query: string): readonly NavItem[] {
  const q = query.trim().toLowerCase()
  if (!q) return NAV_ITEMS

  // Same ranking as the Combobox: a prefix match is what the user meant.
  const scored = NAV_ITEMS.map((item) => {
    const label = item.label.toLowerCase()
    const haystack = `${label} ${item.path} ${item.keywords ?? ''}`.toLowerCase()
    if (label.startsWith(q)) return { item, score: 0 }
    if (haystack.includes(q)) return { item, score: 1 }
    return null
  }).filter((entry): entry is { item: NavItem; score: number } => entry !== null)

  return scored.sort((a, b) => a.score - b.score).map((entry) => entry.item)
}

/**
 * ⌘K navigation.
 *
 * Deliberately limited to navigation for now: a palette that half-works for
 * actions teaches people not to trust it. Actions land when the modules do.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const dialogRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const results = useMemo(() => filterItems(query), [query])

  const close = () => onOpenChange(false)
  useFocusTrap(open, dialogRef, close)

  // Every opening starts from a clean slate — a stale query from last time is
  // never what someone wants to see.
  useEffect(() => {
    if (!open) return
    setQuery('')
    setActiveIndex(0)
    inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({
      block: 'nearest',
    })
  }, [open, activeIndex])

  if (!open) return null

  const go = (item: NavItem | undefined) => {
    if (!item) return
    onOpenChange(false)
    navigate(item.path)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        if (results.length === 0) return
        const delta = event.key === 'ArrowDown' ? 1 : -1
        setActiveIndex((index) => (index + delta + results.length) % results.length)
        return
      }
      case 'Home':
        event.preventDefault()
        setActiveIndex(0)
        return
      case 'End':
        event.preventDefault()
        setActiveIndex(Math.max(results.length - 1, 0))
        return
      case 'Enter':
        event.preventDefault()
        go(results[activeIndex])
        return
      default:
        return
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-[3px]" aria-hidden onClick={close} />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
        className="relative z-10 flex w-full max-w-lg flex-col overflow-hidden rounded-card border border-border bg-surface-raised shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="size-4 shrink-0 text-fg-muted" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            autoComplete="off"
            spellCheck={false}
            aria-expanded
            aria-controls="command-palette-list"
            aria-activedescendant={
              results.length > 0 ? `command-palette-option-${activeIndex}` : undefined
            }
            placeholder="Jump to…"
            aria-label="Search destinations"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setActiveIndex(0)
            }}
            className="h-12 w-full bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle"
          />
        </div>

        <ul
          ref={listRef}
          id="command-palette-list"
          role="listbox"
          aria-label="Destinations"
          className="max-h-80 overflow-y-auto py-1"
        >
          {results.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-fg-muted">No matches</li>
          )}

          {results.map((item, index) => {
            const isActive = index === activeIndex
            const Icon = item.icon
            return (
              <li
                key={item.path}
                id={`command-palette-option-${index}`}
                role="option"
                aria-selected={isActive}
                data-active={isActive}
                onMouseEnter={() => setActiveIndex(index)}
                // pointerdown, not click: click lands after the input blurs.
                onPointerDown={(event) => {
                  event.preventDefault()
                  go(item)
                }}
                className={cn(
                  'flex cursor-pointer items-center gap-3 px-3 py-2 text-sm text-fg',
                  isActive && 'bg-surface-hover',
                )}
              >
                <Icon className="size-4 shrink-0 text-fg-muted" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                <span className="shrink-0 text-xs text-fg-subtle">{item.path}</span>
                {isActive && (
                  <CornerDownLeft className="size-3.5 shrink-0 text-fg-muted" aria-hidden />
                )}
              </li>
            )
          })}
        </ul>

        <div className="flex items-center gap-3 border-t border-border px-3 py-2 text-[11px] text-fg-muted">
          <span>↑↓ to move</span>
          <span>↵ to open</span>
          <span>esc to close</span>
        </div>
      </div>
    </div>
  )
}
